import { ChangeEvent, useEffect, useState } from 'react'

import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import { FormUploadPreview } from '@/core/components/FormUploadPreview'
import { getContinuousQuests } from '@/core/services/gamification'
import { uploadImages } from '@/core/services/upload-image'
import {
  BadgeDto,
  ContinuousQuestSummary,
  RoadmapDto,
  STEP_UNLOCK_MODES,
  StepDto,
  StepUnlockMode,
  TargetDraft,
  emptyTargetDraft,
  stepToTargetDraft,
  targetDraftError,
} from '@/common/models/gamification'

import { StepDependenciesField } from './StepDependenciesField'
import { TargetAuthoring } from './TargetAuthoring'
import { saveStep } from './saveStep'
import { hydrateTargetDraftTags, mergeHydratedTags } from './tagAssociations'

interface StepEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roadmapId: string
  /** Festival edition id — threaded down to the quest editor's token-pool picker. */
  editionId: string
  step?: StepDto | null
  allSteps: StepDto[]
  allRoadmaps: RoadmapDto[]
  badges: BadgeDto[]
  onChanged: () => Promise<void> | void
  onError: (e: unknown) => void
}

export const StepEditorModal = ({
  open,
  onOpenChange,
  roadmapId,
  editionId,
  step,
  allSteps,
  allRoadmaps,
  badges,
  onChanged,
  onError,
}: StepEditorModalProps) => {
  const isEditMode = !!step

  const [unlockMode, setUnlockMode] = useState<StepUnlockMode>('DEFAULT')
  const [targetDraft, setTargetDraft] = useState<TargetDraft>(
    emptyTargetDraft('NONE'),
  )
  const [badgeId, setBadgeId] = useState<string>('')
  const [iconRef, setIconRef] = useState<string>('')
  const [active, setActive] = useState<boolean>(true)
  const [hidden, setHidden] = useState<boolean>(false)
  const [saving, setSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  /**
   * Active continuous quests across the WHOLE edition — the scope the server enforces. The builder
   * only holds this roadmap's steps, so one in a sibling or nested roadmap is invisible without this.
   */
  const [continuousQuests, setContinuousQuests] = useState<
    ContinuousQuestSummary[]
  >([])
  const [uploadError, setUploadError] = useState<string | undefined>()

  useEffect(() => {
    if (!open) return undefined
    setUploadError(undefined)
    setIsUploading(false)

    if (!step) {
      setUnlockMode('DEFAULT')
      setTargetDraft(emptyTargetDraft('NONE'))
      setBadgeId('')
      setIconRef('')
      setActive(true)
      setHidden(false)
      return undefined
    }

    const draft = stepToTargetDraft(step)
    setUnlockMode(step.unlockMode)
    setTargetDraft(draft)
    setBadgeId(step.badgeId ?? '')
    setIconRef(step.iconRef ?? '')
    setActive(step.active)
    setHidden(step.hidden)

    // The quest/quest-group wire responses don't carry `tags` (separate association endpoints)
    // — fetch them best-effort after the synchronous draft is in place so the tags picker shows
    // the currently-attached tags once they resolve.
    let cancelled = false
    hydrateTargetDraftTags(draft)
      .then((hydrated) => {
        // Merge onto the *current* draft (by id) rather than replacing it, so any edits made while
        // this fetch was in flight survive — see mergeHydratedTags.
        if (!cancelled)
          setTargetDraft((prev) => mergeHydratedTags(prev, hydrated))
      })
      .catch(() => {
        // Best-effort only — leave the draft's tags empty on failure.
      })
    return () => {
      cancelled = true
    }
  }, [open, step])

  // Best-effort: on failure the picker simply loses its early warning and the server's 400 (which
  // enforces the same rule) becomes the backstop. Never blocks opening the editor.
  useEffect(() => {
    if (!open || !editionId) return undefined
    let cancelled = false
    getContinuousQuests(editionId)
      .then((res) => {
        if (!cancelled) setContinuousQuests(res.data ?? [])
      })
      .catch(() => {
        if (!cancelled) setContinuousQuests([])
      })
    return () => {
      cancelled = true
    }
  }, [open, editionId])

  const handleIconUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 1_000_000) {
      setUploadError('The icon is too large (max 1 MB)')
      return
    }
    setUploadError(undefined)
    setIsUploading(true)
    try {
      const { data } = await uploadImages(file, true, 'GAMIFICATION_STEP_ICON')
      if (data?.photoUrl) setIconRef(data.photoUrl)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      setUploadError(
        status === 400
          ? 'Icon must be at least 42x42 px (square)'
          : 'Icon upload failed',
      )
    } finally {
      setIsUploading(false)
    }
  }

  const invalid = !!targetDraftError(targetDraft)

  const handleClose = () => onOpenChange(false)

  const submit = async () => {
    if (invalid) return
    try {
      setSaving(true)
      await saveStep({
        roadmapId,
        original: step ?? null,
        stepFields: {
          unlockMode,
          badgeId,
          iconRef,
          active,
          hidden,
          // Preserve ordinal on edit; let the server append on create.
          ordinal: step?.ordinal,
        },
        draft: targetDraft,
        onDraftChange: setTargetDraft,
      })
      await onChanged()
      handleClose()
    } catch (e) {
      onError(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
      size="lg"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="flash"
            title={
              <span className="flex items-center gap-2">
                {isEditMode ? 'Edit' : 'Add New'}{' '}
                <span className="text-accent-1">Step</span>
              </span>
            }
          />
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={handleClose} type="button" variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={submit}
            type="button"
            variant="primary"
            disabled={invalid || saving || isUploading}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <SelectField
          label="Unlock mode"
          required
          value={unlockMode}
          onValueChange={(v) => setUnlockMode(v as StepUnlockMode)}
          options={STEP_UNLOCK_MODES.map((m) => ({ value: m, label: m }))}
        />

        <TargetAuthoring
          value={targetDraft}
          onChange={setTargetDraft}
          allRoadmaps={allRoadmaps}
          currentRoadmapId={roadmapId}
          continuousQuests={continuousQuests}
          editionId={editionId}
        />

        <SelectField
          label="Badge"
          placeholder="No badge"
          value={badgeId || 'NONE'}
          onValueChange={(v) => setBadgeId(v === 'NONE' ? '' : v)}
          options={[
            { value: 'NONE', label: 'No badge' },
            ...badges.map((b) => ({ value: b.id, label: b.name })),
          ]}
        />

        <div>
          <FormUploadPreview
            label="Icon (42×42)"
            previewLabel="Icon (42×42)"
            isUploading={isUploading}
            url={iconRef}
            width={42}
            height={42}
            imageName={iconRef ? 'step icon' : undefined}
            onRemove={() => setIconRef('')}
            imageStyle={{ objectFit: 'contain' }}
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={!!uploadError}
                type="file"
                className="bg-monochrome-5-50"
                onChange={handleIconUpload}
              />
            }
          />
          {uploadError && (
            <span className="text-xs text-accent-3">{uploadError}</span>
          )}
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={active}
            onCheckedChange={(checked) => setActive(!!checked)}
          />
          <span className="text-body-medium text-monochrome-1">Active</span>
        </label>

        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={hidden}
            onCheckedChange={(checked) => setHidden(!!checked)}
          />
          <span className="text-body-medium text-monochrome-1">
            Hidden from roadmap
            <span className="block text-xs text-monochrome-2">
              Step stays active (quests fire, pools fill) but is not shown to
              users.
            </span>
          </span>
        </label>

        {isEditMode && step ? (
          <StepDependenciesField
            step={step}
            allSteps={allSteps}
            onChanged={onChanged}
            onError={onError}
          />
        ) : (
          <div className="border-t border-monochrome-120 pt-6 text-xs text-monochrome-2">
            Save the step first, then re-open it to configure dependencies.
          </div>
        )}
      </div>
    </Modal>
  )
}
